/**
 * Glass sticky header with page title, SignalR indicator, notifications, and theme toggle.
 */

import { LogOut, User } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { NotificationBell } from "@/components/layout/notification-bell";
import { HelpButton } from "@/components/layout/help-button";
import { SignalRIndicator } from "@/components/layout/signalr-indicator";

interface HeaderProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly onHelpToggle?: () => void;
}

export function Header({ title, subtitle, onHelpToggle }: HeaderProps) {
  const { displayName, logout } = useAuth();

  return (
    <header className="sticky top-0 z-30 flex items-center justify-between h-header px-8 bg-sc-surface/60 backdrop-blur-[16px] border-b border-sc-border">
      {/* Left: page title */}
      <div>
        <h1 className="text-h1 text-sc-navy font-display">{title}</h1>
        {subtitle && (
          <p className="text-body-sm text-sc-text-muted mt-0.5">{subtitle}</p>
        )}
      </div>

      {/* Right: SignalR indicator + notifications + theme + user */}
      <div className="flex items-center gap-3">
        <SignalRIndicator />
        <NotificationBell count={0} />
        <ThemeToggle />
        {onHelpToggle && <HelpButton onClick={onHelpToggle} />}

        <div className="flex items-center gap-3 pl-3 border-l border-sc-border">
          <div className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-gradient-to-br from-sc-green to-sc-navy text-white">
            <User size={16} />
          </div>
          <div className="hidden sm:block">
            <p className="text-body-sm font-medium text-sc-text leading-tight">
              {displayName ?? "User"}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => void logout()}
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut size={18} />
          </Button>
        </div>
      </div>
    </header>
  );
}
